import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { cn } from "@/lib/utils";
import IncomeTaxChart from '../../charts/IncomeTaxChart';
import { IncomeTaxCalculatorAPIResponse } from '../../Types/Autogenerated/IncomeTaxCalculatorApiResponse';
import { IncomeTaxCalculatorRequestPayload } from '../../Types/Autogenerated/IncomeTaxCalculatorRequestPayload';
import { IncomesAdjustmentsAndDeductions } from '../../Types/Autogenerated/IncomesAdjustmentsAndDeductions';
import { Convert as FilingStatusConvert } from '../../Types/Autogenerated/FilingStatus';
import { Convert as StateConvert } from '../../Types/Autogenerated/State';
import { FormData } from '../../App';
import { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import { taxYearItems, stateItems, filingStatuses } from '../../Utils/Constants';
import TermsDescriptions from '../../components/TermsDescriptions';
import { incomeTaxTerms } from '../../Utils/TermDefinitions';

interface IncomeTaxCalculatorPageProps {
  formData: FormData;
  selectedPage: string;
  personsToggle: boolean;
  showSnackbar: boolean;
  snackbarMessage: string;
  showChart: boolean;
  chartData: number[][] | null;
  handleClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
  setChartData: (value: React.SetStateAction<number[][] | null>) => void;
  setShowChart: (value: React.SetStateAction<boolean>) => void;
  setSnackbarMessage: (value: React.SetStateAction<string>) => void;
  setShowSnackbar: (value: React.SetStateAction<boolean>) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange: (value: string | undefined, name?: string, values?: CurrencyInputOnChangeValues) => void;
  handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const IncomeTaxCalculatorPage: React.FC<IncomeTaxCalculatorPageProps> = ({
  formData,
  selectedPage,
  personsToggle,
  showSnackbar,
  snackbarMessage,
  showChart,
  chartData,
  handleClose,
  setChartData,
  setShowChart,
  setSnackbarMessage,
  setShowSnackbar,
  handleInputChange,
  onValueChange,
  handleToggleChange,
  handleSelectChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const formatNumber = (num: number | string) => {
    if (typeof num === 'string') {
      num = parseFloat(num.replace(/,/g, ''));
    }
    return isNaN(num) ? '' : num.toLocaleString('en-US');
  };

  const handleIncomeChange = (value: string, name: string) => {
    const numericValue = value ? parseFloat(value.replace(/,/g, '')) : 0;
    onValueChange(numericValue.toString(), name);
  };

  const processChartData = useCallback((data: IncomeTaxCalculatorAPIResponse): number[][] => {
    return [[
      data.medicare.income_tax_owed.reduce((a, b) => a + b, 0),
      data.social_security.income_tax_owed.reduce((a, b) => a + b, 0),
      data.state.income_tax_owed.reduce((a, b) => a + b, 0),
      data.federal.income_tax_owed.reduce((a, b) => a + b, 0),
      data.total_tax_owed,
    ]];
  }, []);

  const makeIncomeTaxCalculatorUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://127.0.0.1:5000/TakehomePayCalculator';
    }
    
    // Use predictable CloudFront URL
    const domain = window.location.hostname === 'moneymath.fyi' 
      ? 'moneymath.fyi' 
      : window.location.hostname;
    
    return `https://${domain}/api/takehome-pay`;
  };

  // Memoize input data to prevent unnecessary re-renders
  const inputData = useMemo(() => ({
    income: personsToggle ? [formData.incomePerson1, formData.incomePerson2] : [formData.income],
    taxYear: formData.taxYear,
    state: formData.state,
    filingStatus: formData.filingStatus,
    personsToggle
  }), [
    formData.income,
    formData.incomePerson1,
    formData.incomePerson2,
    formData.taxYear,
    formData.state,
    formData.filingStatus,
    personsToggle
  ]);

  const makeAPIRequest = useCallback(async () => {
    if (isLoading) return; // Prevent multiple simultaneous requests
    
    setIsLoading(true);
    try {
      const url = makeIncomeTaxCalculatorUrl();

      const incomes_adjustments_and_deductions: IncomesAdjustmentsAndDeductions[] = !inputData.personsToggle
        ? [{
            salaries_and_wages: Number(inputData.income[0]),
          }]
        : [{
            salaries_and_wages: Number(inputData.income[0]),
          }, {
            salaries_and_wages: Number(inputData.income[1]),
          }];

      const payload: IncomeTaxCalculatorRequestPayload = {
        tax_year: parseInt(inputData.taxYear, 10),
        state: StateConvert.toState(JSON.stringify(inputData.state)),
        filing_status: FilingStatusConvert.toFilingStatus(JSON.stringify(inputData.filingStatus)),
        incomes_adjustments_and_deductions: incomes_adjustments_and_deductions,
        state_data: { exemptions: 0 }
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unknown error occurred');
      }

      const data: IncomeTaxCalculatorAPIResponse = await response.json();
      setChartData(processChartData(data));
      setShowChart(true);
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'An unknown error occurred');
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [inputData, processChartData, setChartData, setShowChart, setSnackbarMessage, setShowSnackbar, isLoading]);

  // Track previous input data for change detection
  const previousInputDataRef = useRef<string>('');

  // Auto-calculate with debouncing for API calls - only when inputs actually change
  useEffect(() => {
    const currentInputDataString = JSON.stringify(inputData);
    
    // Only make API request if input data has actually changed
    if (currentInputDataString !== previousInputDataRef.current) {
      const timeoutId = setTimeout(() => {
        makeAPIRequest();
        previousInputDataRef.current = currentInputDataString;
      }, 500); // 500ms debounce for quick API calls

      return () => clearTimeout(timeoutId);
    }
  }, [inputData, makeAPIRequest]);

  const labels: string[] = [
    "Medicare",
    "Social Security",
    "State",
    "Federal",
    "Total Tax",
  ];

  const sortedTaxYears = Object.entries(taxYearItems)
    .sort((a, b) => parseInt(b[1]) - parseInt(a[1]));

  const chartRef = useRef<HTMLDivElement>(null);

  return (
    <div className="income-tax-calculator w-full px-1 sm:px-2 lg:px-3 h-full flex flex-col">
      <div className="flex flex-col lg:flex-row gap-4 h-full mt-16 sm:mt-20 md:mt-24 mb-4">
        {/* Form Section */}
        <div className="lg:w-1/3 min-w-0">
          <Card className="w-full flex-grow overflow-hidden flex flex-col">
            <CardHeader>
              <h2 className={cn("text-2xl font-bold")}>{selectedPage}</h2>
            </CardHeader>
            <CardContent className="flex-grow overflow-y-auto">
              <div className={cn("space-y-4")}>
                <div className={cn("flex items-center justify-between")}>
                  <Label htmlFor="personsToggle" className="text-left w-full">Two Incomes</Label>
                  <Switch
                    id="personsToggle"
                    checked={personsToggle}
                    onCheckedChange={(checked) => handleToggleChange({
                      target: { name: 'personsToggle', checked, id: 'persons', type: 'checkbox' }
                    } as React.ChangeEvent<HTMLInputElement>)}
                  />
                </div>
                
                <div className={cn("grid gap-4", personsToggle ? "grid-cols-2" : "grid-cols-1")}>
                  <div>
                    <Label htmlFor={personsToggle ? "incomePerson1" : "income"} className="text-left w-full">
                      {personsToggle ? "Income 1" : "Income"}
                    </Label>
                    <div className={cn("relative")}>
                      <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-base md:text-sm")}>$</span>
                      <Input
                        id={personsToggle ? "incomePerson1" : "income"}
                        name={personsToggle ? "incomePerson1" : "income"}
                        type="text"
                        value={formatNumber(personsToggle ? formData.incomePerson1 : formData.income)}
                        onChange={(e) => handleIncomeChange(e.target.value, personsToggle ? 'incomePerson1' : 'income')}
                        className={cn("pl-6")}
                      />
                    </div>
                  </div>

                  {personsToggle && (
                    <div>
                      <Label htmlFor="incomePerson2" className="text-left w-full">Income 2</Label>
                      <div className={cn("relative")}>
                        <span className={cn("absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-base md:text-sm")}>$</span>
                        <Input
                          id="incomePerson2"
                          name="incomePerson2"
                          type="text"
                          value={formatNumber(formData.incomePerson2)}
                          onChange={(e) => handleIncomeChange(e.target.value, 'incomePerson2')}
                          className={cn("pl-6")}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className={cn("grid grid-cols-1 gap-4")}>
                  <div>
                    <Label htmlFor="taxYear" className="text-left w-full">Tax Year</Label>
                    <Select 
                      value={formData.taxYear} 
                      onValueChange={(value) => handleSelectChange({ target: { name: 'taxYear', value } } as React.ChangeEvent<HTMLSelectElement>)}
                    >
                      <SelectTrigger id="taxYear">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedTaxYears.map(([key, value]) => (
                          <SelectItem key={value} value={value}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-left w-full">State</Label>
                    <Select 
                      value={formData.state} 
                      onValueChange={(value) => handleSelectChange({ target: { name: 'state', value } } as React.ChangeEvent<HTMLSelectElement>)}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(stateItems).map(([key, value]) => (
                          <SelectItem key={value} value={value}>{key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="filingStatus" className="text-left w-full">Filing Status</Label>
                  <Select 
                    value={formData.filingStatus} 
                    onValueChange={(value) => handleSelectChange({ target: { name: 'filingStatus', value } } as React.ChangeEvent<HTMLSelectElement>)}
                  >
                    <SelectTrigger id="filingStatus">
                      <SelectValue placeholder="Select filing status" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(filingStatuses).map(([key, value]) => (
                        <SelectItem key={key} value={key}>{value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart Section */}
        <div className="lg:w-2/3 min-w-0" ref={chartRef}>
          <Card className="w-full lg:h-[calc(100vh-200px)] h-auto">
            <CardContent className="p-4 h-full">
              {chartData && !isLoading ? (
                <div className="h-full flex flex-col">
                  <div className="flex-grow min-h-0">
                    <IncomeTaxChart labels={labels} chartData={chartData} />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-2">
                    {isLoading && (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    )}
                    <div className="text-gray-500">
                      {isLoading ? 'Calculating taxes...' : 'Loading chart...'}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Terms Descriptions Section */}
      <TermsDescriptions 
        terms={incomeTaxTerms} 
        title="Income Tax Calculator Terms" 
      />
      
      {showSnackbar && (
        <div className={cn("fixed bottom-4 left-4 right-4 bg-red-500 text-white p-4 rounded-md")}>
          {snackbarMessage}
        </div>
      )}
    </div>
  );
};

export default IncomeTaxCalculatorPage;
